import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChevronLeft,
  Loader2,
  PlayCircle,
  PauseCircle,
  RefreshCcw,
  Trash2,
  CheckCircle2,
  XCircle,
  Send,
  ExternalLink,
  CalendarDays,
} from "lucide-react";
import JobProgressBar from "@/components/JobProgressBar";

const STATUS_BADGE = {
  active: "bg-emerald-600",
  paused: "bg-amber-500",
  cancelled: "bg-stone-500",
  completed: "bg-sky-600",
};

const TOPIC_BADGE = {
  queued: "bg-stone-300 text-stone-700",
  generating: "bg-amber-200 text-amber-900",
  ready: "bg-emerald-200 text-emerald-900",
  publishing: "bg-sky-200 text-sky-900",
  published: "bg-red-200 text-red-900",
  failed: "bg-rose-200 text-rose-900",
  skipped: "bg-stone-200 text-stone-700",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function SeriesDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  // Phase1Observability1:progress_eta_v1 — per-topic video doc cache for
  // the inline progress bar. Only fetched for non-terminal topics.
  const [videoById, setVideoById] = useState({});

  const refresh = async () => {
    try {
      const [seriesR, eventsR] = await Promise.all([
        api.get(`/series/${id}`),
        api.get(`/series/${id}/events?limit=50`),
      ]);
      setS(seriesR.data);
      setEvents(eventsR.data?.events || []);
      // Fetch underlying video docs for any topic that has a video_id
      // AND isn't in a terminal state. Cheap: 1 GET per non-terminal topic.
      const TERMINAL = new Set([
        "ready", "published", "skipped", "cancelled",
      ]);
      const targets = (seriesR.data?.topics || [])
        .filter((t) => t.video_id && !TERMINAL.has(t.status))
        .map((t) => t.video_id);
      if (targets.length > 0) {
        const results = await Promise.all(
          targets.map((vid) =>
            api.get(`/videos/${vid}`).then((r) => r.data).catch(() => null),
          ),
        );
        const map = {};
        results.forEach((v) => {
          if (v && v.id) map[v.id] = v;
        });
        setVideoById(map);
      } else {
        setVideoById({});
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (path, method = "POST") => {
    setBusy(path);
    setError(null);
    try {
      await api.request({ url: `/series/${id}${path}`, method });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!s) {
    return (
      <div className="flex items-center gap-2 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> loading…
      </div>
    );
  }

  const total = s.topics.length;
  const done = s.topics.filter((t) =>
    ["ready", "published"].includes(t.status),
  ).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div data-testid={`series-detail-${id}`} className="space-y-6">
      <Link
        to="/series"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-orange-600"
      >
        <ChevronLeft className="h-4 w-4" /> All series
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-stone-800">{s.name}</h1>
            <Badge className={STATUS_BADGE[s.status] || "bg-stone-500"}>
              {s.status}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-stone-600">
            {s.content_type} · {s.length} · {total} topics ·{" "}
            <span className="font-semibold">${s.actual_cost_usd?.toFixed(2)}</span>
            <span className="text-stone-400"> / est ${s.estimated_cost_usd?.toFixed(2)}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {s.status === "active" ? (
            <Button
              variant="outline"
              data-testid="series-pause"
              onClick={() => act("/pause")}
              disabled={busy === "/pause"}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <PauseCircle className="mr-1 h-4 w-4" /> Pause
            </Button>
          ) : null}
          {s.status === "paused" ? (
            <Button
              data-testid="series-resume"
              onClick={() => act("/resume")}
              disabled={busy === "/resume"}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <PlayCircle className="mr-1 h-4 w-4" /> Resume
            </Button>
          ) : null}
          {s.status !== "cancelled" && s.status !== "completed" ? (
            <Button
              variant="outline"
              data-testid="series-cancel"
              onClick={() => {
                if (window.confirm("Cancel this series?")) act("", "DELETE");
              }}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="mr-1 h-4 w-4" /> Cancel
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="border-orange-100 bg-white/80">
        <CardHeader>
          <CardTitle className="text-base">Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-1 flex justify-between text-xs text-stone-600">
            <span>{done}/{total} ready or published</span>
            <span className="font-semibold">{pct}%</span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-stone-100">
            <div
              data-testid="series-progress"
              className="h-full rounded-full bg-gradient-to-r from-orange-500 to-rose-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-stone-500">
            <CalendarDays className="mr-1 inline h-3 w-3" />
            {s.schedule?.cadence} at {s.schedule?.time_local} ({s.schedule?.timezone}) ·{" "}
            visibility: <strong>{s.schedule?.publish_visibility}</strong>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <div
          data-testid="series-error"
          className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      ) : null}

      <div className="grid gap-3">
        {s.topics.map((t, i) => (
          <Card
            key={i}
            data-testid={`topic-${i}`}
            className="border-orange-100 bg-white/80"
          >
            <CardContent className="flex flex-wrap items-center gap-3 p-4">
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                {i + 1}
              </span>
              <div className="flex-1">
                <div className="font-semibold text-stone-800">{t.title}</div>
                <div className="text-xs text-stone-500">
                  Scheduled: {fmt(t.scheduled_publish_at)}
                  {t.error ? (
                    <span className="ml-2 text-red-600">
                      · {t.error.slice(0, 80)}
                    </span>
                  ) : null}
                </div>
              </div>
              <span
                className={`rounded px-2 py-0.5 text-xs ${
                  TOPIC_BADGE[t.status] || "bg-stone-200 text-stone-700"
                }`}
              >
                {t.status === "generating" ? (
                  <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                ) : t.status === "ready" ? (
                  <CheckCircle2 className="mr-1 inline h-3 w-3" />
                ) : t.status === "published" ? (
                  <PlayCircle className="mr-1 inline h-3 w-3" />
                ) : t.status === "failed" ? (
                  <XCircle className="mr-1 inline h-3 w-3" />
                ) : null}
                {t.status}
              </span>
              {t.video_id ? (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  data-testid={`topic-${i}-open`}
                >
                  <Link to={`/library?open=${t.video_id}`}>open</Link>
                </Button>
              ) : null}
              {/* Phase1Observability1:progress_eta_v1 — full-width row below */}
              {t.video_id && videoById[t.video_id] ? (
                <div className="basis-full pt-1">
                  <JobProgressBar video={videoById[t.video_id]} compact />
                </div>
              ) : null}
              {t.status === "ready" ? (
                <Button
                  size="sm"
                  data-testid={`topic-${i}-publish-now`}
                  onClick={() => act(`/publish-now/${i}`)}
                  disabled={busy?.includes(`/publish-now/${i}`)}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  <Send className="mr-1 h-3.5 w-3.5" /> Publish now
                </Button>
              ) : null}
              {["ready", "failed"].includes(t.status) ? (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`topic-${i}-regen`}
                  onClick={() => act(`/regenerate/${i}`)}
                  disabled={busy?.includes(`/regenerate/${i}`)}
                  className="border-orange-300"
                >
                  <RefreshCcw className="mr-1 h-3.5 w-3.5" /> Regenerate
                </Button>
              ) : null}
              {t.youtube_url ? (
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-emerald-300 text-emerald-700"
                >
                  <a href={t.youtube_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="mr-1 h-3.5 w-3.5" /> YouTube
                  </a>
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {events.length > 0 ? (
        <Card className="border-orange-100 bg-white/60">
          <CardHeader>
            <CardTitle className="text-base">Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-xs text-stone-600">
              {events.slice(0, 30).map((e) => (
                <li key={e.id} className="flex justify-between gap-3">
                  <span>
                    <span className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[10px]">
                      {e.kind}
                    </span>{" "}
                    {e.payload?.video_id ? (
                      <code className="text-[10px] text-stone-400">
                        {e.payload.video_id.slice(0, 8)}
                      </code>
                    ) : null}
                    {e.payload?.error ? (
                      <span className="ml-1 text-red-600">
                        — {String(e.payload.error).slice(0, 80)}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-stone-400">{fmt(e.at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
